import type { MutableState, State } from 'src/replicant/State';
import {
  getActiveRace,
  getActiveRaceState,
  getRaceEndDate,
} from './races.getters';
import { duration } from 'src/replicant/utils/duration';
import { assertNever } from 'src/replicant/utils';
import { createPrng } from '@play-co/replicant';
import type { BotState, RaceState } from './races.schema';
import {
  getRandomFloat,
  getRandomInt,
  getRandomItemFromArray,
} from 'src/replicant/utils/random';
import { BotEventType, botCompositionsV2 } from './races.ruleset';

/**
 * This APIs allow get rid of the heartbeats race bot logic.
 * We can simulate this logic on each game end event, instead calling it every 30 seconds.
 *
 * The bots have two logic sets: online and offline.
 *
 * We should use this public API when:
 * updateRaceBotsOnGameEnd - called on each game end event
 * updateRaceBotsOnLogin - called on each login
 * updateRaceBotsOnPopupOpen - called before we show any rewards or leaderboards UI
 */

/**
 * Calculate online time for online bots
 */
export function calculateOnlineTime(state: State, now: number): number {
  // We need detect if we are online now or offline
  // For this we compere last race online time and offline time
  // If offlineAt time is behind onlineAt we are online
  if (
    state.match2.raceOnlineAt > state.match2.raceOfflineAt &&
    state.match2.raceBotUpdateAt >= state.match2.raceOnlineAt
  ) {
    return Math.max(0, now - state.match2.raceBotUpdateAt);
  }

  return 0;
}

/**
 * Called on each login. Update offline bot logic.
 */
export function updateRaceBotsOnLogin(state: MutableState, now: number) {
  // Update offline bots logic, we doing this only on login
  // Also we check if user properly  finished end game session, in cases if this was just game reload
  // raceOfflineAt and raceOnlineAt can be zero, in this case we need to update bots
  if (state.match2.raceOfflineAt >= state.match2.raceOnlineAt) {
    // Update offline bots logic
    updateBotsOnLogin(state, now);
    // Last update time
    state.match2.raceBotUpdateAt = now;
    // Update online time when we are online
    state.match2.raceOnlineAt = now;
  }
}

/**
 * Called before any race UI opened.
 * Updated online bot logic before showing any rewards or leaderboards.
 */
export function updateRaceBotsOnPopupOpen(state: MutableState, now: number) {
  updateBotsOnline(state, now);
}

/**
 * Called on each game end event.
 * Update last time online bots logic.
 */
export function updateRaceBotsOnGameEnd(state: MutableState, now: number) {
  // Last time update bots online logic
  updateBotsOnline(state, now);

  // Update offline time, it will help to detect online/offline state
  state.match2.raceOfflineAt = now;
}

/**
 * Bots online update logic.
 * Should simulate previous heartbeat logic.
 *
 * Performance optimization (Issue #10078):
 * Cap the number of iterations based on the race's remaining duration to prevent
 * freezes with large time shifts (1-3+ months). The key insight is that races
 * have limited duration (1-2 hours), so bot calculations should stop when the
 * race ends. This preserves the original scoring behavior while preventing
 * the O(n) disaster where n could be 259,200+ for a 3-month time shift.
 */
function updateBotsOnline(state: MutableState, now: number) {
  const interval = duration({ seconds: 30 });
  const onlineTime = calculateOnlineTime(state, now);

  // Early exit if no online time
  if (onlineTime <= 0) {
    state.match2.raceBotUpdateAt = now;
    return;
  }

  // Get active race state to determine race duration cap
  const activeRaceState = getActiveRaceState(state, now);
  if (!activeRaceState) {
    state.match2.raceBotUpdateAt = now;
    return;
  }
  const raceId = activeRaceState.id;
  const lastRaceState = activeRaceState;

  // PERFORMANCE FIX: Cap iterations based on race window
  // The race has a finite duration, so we should only iterate for time
  // within the race window, not for months of accumulated "online time"
  const raceEndTime = getRaceEndDate(raceId);
  const raceStartTime = lastRaceState.createdAt;

  // Calculate the maximum sensible iterations based on race duration
  // Races typically last 1-2 hours, so max ~240 iterations (2 hours / 30 sec)
  const raceDuration = raceEndTime - raceStartTime;
  const maxIterations = Math.ceil(raceDuration / interval);

  // Calculate actual iterations from online time, but cap to race duration
  const requestedIterations = Math.floor(onlineTime / interval);
  const iterations = Math.min(requestedIterations, maxIterations);

  // Simulate bots update for 30 seconds intervals
  // Preserve original time formula (now + i * interval) for scoring behavior
  for (let i = 0; i < iterations; i++) {
    const time = now + i * interval;
    tickRaceBots('onHeartbeat', state, time);
  }

  // Update time when the last iteration was done
  state.match2.raceBotUpdateAt = now;
}

/**
 * Use this API only for unit tests
 * @deprecated
 */
export const BotsPrivateApi = {
  tickRaceBots,
  updateRaceBotsOnLogin,
};

/** -----------------------------------
 * Old bots logic above:
 * -----------------------------------/

/**
 * Tick bots while player is online.
 * @param state
 * @param now
 */
function tickRaceBots(
  eventType: BotEventType,
  state: MutableState,
  now: number,
) {
  const activeRaceState = getActiveRaceState(state, now) as RaceState;

  // No active race, bail.
  if (!activeRaceState) return;

  // No bots, bail.
  if (!activeRaceState.bots || activeRaceState.bots.length === 0) return;

  // Cap now to the duration of the race.
  now = Math.min(now, getRaceEndDate(activeRaceState.id));

  // Check if three players have already reached the target. And cap now to the third player's modifiedAt.
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  if (config.type === 'target') {
    const targetBots = activeRaceState.bots.filter(
      (x) => x.score >= config.target,
    );
    if (targetBots.length >= 3) {
      targetBots.sort((a, b) => a.score - b.score);
      now = Math.min(now, targetBots[2].modifiedAt);
    }
  }

  // Get bot comp.
  const raceRNG = createPrng(`${activeRaceState.createdAt}`);
  const composition = getRandomItemFromArray(botCompositionsV2, raceRNG);

  // Tick bots.
  activeRaceState.bots.forEach((bot, i) => {
    const botType = composition[i];
    switch (botType) {
      case 'default':
        tickDefaultBot(eventType, i, activeRaceState, bot, now);
        break;
      case 'competitive':
        tickCompetitiveBot(eventType, i, state, activeRaceState, bot, now);
        break;
      case 'dud':
        tickDudBot(eventType, activeRaceState, bot, now);
        break;

      default:
        assertNever(botType);
    }

    // Cap score at target.
    if (config.type === 'target') {
      bot.score = Math.min(bot.score, config.target);
    }
  });
}

/**
 * Tick default bot while player is online.
 */
function tickDefaultBot(
  eventType: BotEventType,
  botIndex: number,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  // Only tick default bots on heartbeat.
  if (eventType !== 'onHeartbeat') return;

  // Get race config.
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  const rng = createPrng(`${bot.modifiedAt + botIndex}`);

  switch (config.goal) {
    // Bot will score 1 point after every X seconds,
    // X is random integer between 120 and 270 chosen every time.
    case 'level': {
      const updateScoreAt = () =>
        bot.modifiedAt +
        getRandomInt(
          duration({ seconds: 120 }),
          duration({ seconds: 270 }),
          rng,
        );
      while (now >= updateScoreAt()) {
        bot.score += 1;
        bot.modifiedAt = updateScoreAt();
      }

      break;
    }

    // Bot will score Y points after every X seconds,
    // Y is a random integer between 30 and 60
    // X is random integer between 120 and 270 chosen every time.
    case 'lollipop': {
      const updateScoreAt = () =>
        bot.modifiedAt +
        getRandomInt(
          duration({ seconds: 120 }),
          duration({ seconds: 270 }),
          rng,
        );
      while (now >= updateScoreAt()) {
        bot.score += getRandomInt(30, 60, rng);
        bot.modifiedAt = updateScoreAt();
      }

      break;
    }

    default:
      assertNever(config);
  }
}

/**
 * Tick competitive bot while player is online.
 */
function tickCompetitiveBot(
  eventType: BotEventType,
  botIndex: number,
  state: MutableState,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  // Only tick default bots on heartbeat.
  if (eventType !== 'onPlayerScored') return;

  // Get race config.
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  const rng = createPrng(`${now + botIndex}`);

  switch (config.goal) {
    // Bot has a 60% chance to add a score every time the player adds a score
    // If the bot is supposed to add a score, it will add either 1 or 2 points randomly picked.
    // So on average if the player adds 10 points in a session, the bot should add ~10 points as well.
    case 'level': {
      if (rng() < 0.6) {
        bot.score += getRandomInt(1, 2, rng);
        bot.modifiedAt = now;
      }
      break;
    }

    // Bot has a 60% chance to add a score every time the player adds a score
    // If the bot is supposed to add a score, it will add = added player's score * Random number between (1.3 and 1.7)
    // So on average if the player adds 100 points in a session, the bot should add ~100 points as well.
    case 'lollipop': {
      if (rng() < 0.6) {
        const addedScore = state.match2.pendingRaceScore?.score ?? 0;
        bot.score += Math.round(addedScore * getRandomFloat(1.3, 1.7, rng));
        bot.modifiedAt = now;
      }
      break;
    }

    default:
      assertNever(config);
  }
}

/**
 * Tick dud bot while player is online.
 */
function tickDudBot(
  eventType: BotEventType,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  // Only tick default bots on heartbeat.
  if (eventType !== 'onHeartbeat') return;

  // Get race config.
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  switch (config.goal) {
    // Give 1 level every 30 minutes.
    case 'level': {
      const updateScoreAt = () => bot.modifiedAt + duration({ minutes: 30 });
      while (now >= updateScoreAt()) {
        bot.score += 1;
        bot.modifiedAt = updateScoreAt();
      }
      break;
    }

    // Give 10 sugar every 15 minutes.
    case 'lollipop': {
      const updateScoreAt = () => bot.modifiedAt + duration({ minutes: 15 });
      while (now >= updateScoreAt()) {
        bot.score += 10;
        bot.modifiedAt = updateScoreAt();
      }
      break;
    }

    default:
      assertNever(config);
  }
}

/**
 * Accrue bots score while player is offline. Meant to be called on login.
 */
function updateBotsOnLogin(state: MutableState, now: number) {
  // Get latest race id.
  const raceId = Object.keys(state.match2.races).sort().pop();

  // User hasn't participated in any race.
  if (!raceId) return;

  // Get latest race state.
  const races = state.match2.races[raceId];
  const lastRaceState = races[races.length - 1];

  // Already claimed rewards, bail.
  if (lastRaceState.claimedRewardAt) return;

  // No bots, bail.
  if (!lastRaceState.bots || lastRaceState.bots.length === 0) return;

  // Cap now to the duration of the race.
  now = Math.min(now, getRaceEndDate(raceId));

  // Check if three players have already reached the target. And cap now to the third player's modifiedAt.
  const { config } = getActiveRace(lastRaceState.createdAt)!;
  if (config.type === 'target') {
    const targetBots = lastRaceState.bots.filter(
      (x) => x.score >= config.target,
    );
    if (targetBots.length >= 3) {
      targetBots.sort((a, b) => a.score - b.score);
      now = Math.min(now, targetBots[2].modifiedAt);
    }
  }

  // Get bot comp.
  const raceRNG = createPrng(`${lastRaceState.createdAt}`);
  const composition = getRandomItemFromArray(botCompositionsV2, raceRNG);

  // Tick bots.
  lastRaceState.bots.forEach((bot, i) => {
    const botType = composition[i];
    switch (botType) {
      case 'default':
        updateDefaultBotsOnLogin(state, i, lastRaceState, bot, now);
        break;

      case 'competitive':
        updateCompetitiveBotsOnLogin(state, i, lastRaceState, bot, now);
        break;

      case 'dud':
        updateDudBotsOnLogin(lastRaceState, bot, now);
        break;

      default:
        assertNever(botType);
    }

    // Cap score at target.
    if (config.type === 'target') {
      bot.score = Math.min(bot.score, config.target);
    }
  });
}

/**
 * Update default bots on login.
 */
function updateDefaultBotsOnLogin(
  state: MutableState,
  botIndex: number,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  // Get race config.
  const { config } = getActiveRace(activeRaceState.createdAt)!;

  const raceType = config.type;
  switch (raceType) {
    case 'time':
      updateDefaultTimedRaceBotsOnLogin(
        state,
        botIndex,
        activeRaceState,
        bot,
        now,
      );
      break;

    case 'target':
      updateDefaultTargetRaceBotsOnLogin(
        state,
        botIndex,
        activeRaceState,
        bot,
        now,
      );
      break;

    default:
      assertNever(raceType);
  }
}

/**
 * Update timed race bots on login.
 */
function updateDefaultTimedRaceBotsOnLogin(
  state: MutableState,
  botIndex: number,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  const timeAway = Math.max(now - state.lastSessionAt, 0);
  const rng = createPrng(`${now + botIndex}`);

  switch (config.goal) {
    // If the player is offline -> bot will score additional points = ROUND(time the player was away / random integer between(7 and 13 minutes))
    // So if the player was away for 35 minutes and random int is 11, the score added will be 3. This is an addition on top of the existing score
    case 'level': {
      const randomValue = getRandomInt(
        duration({ minutes: 7 }),
        duration({ minutes: 13 }),
        rng,
      );
      const scoreToAdd = Math.round(timeAway / randomValue);
      if (scoreToAdd > 0) {
        bot.score += scoreToAdd;
        bot.modifiedAt = now;
      }
      break;
    }

    // If the player is offline -> bot will score additional points = ROUND(random integer between (30 and 60) * time the player was away / random integer between(7 and 13 minutes))
    // So if the player was away for 35 minutes, random score is 43 and random int is 11, the score added will be 136. This is an addition on top of the existing score
    case 'lollipop': {
      const randomValue = getRandomInt(
        duration({ minutes: 7 }),
        duration({ minutes: 13 }),
        rng,
      );
      const randomScore = getRandomInt(30, 60, rng);
      const scoreToAdd = Math.round((randomScore * timeAway) / randomValue);
      if (scoreToAdd > 0) {
        bot.score += scoreToAdd;
        bot.modifiedAt = now;
      }
      break;
    }

    default:
      assertNever(config);
  }
}

/**
 * Update target race bots on login.
 */
function updateDefaultTargetRaceBotsOnLogin(
  state: MutableState,
  botIndex: number,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  const timeAway = Math.max(now - state.lastSessionAt, 0);
  const rng = createPrng(`${now + botIndex}`);

  switch (config.goal) {
    // If the player is offline -> bot will score additional points = time the player was away / random integer between(30 and 90 minutes)
    // So if the player was away for 200 minutes and random int is 44, the score added will be 5. This is an addition on top of the existing score
    case 'level': {
      const randomValue = getRandomInt(
        duration({ minutes: 30 }),
        duration({ minutes: 90 }),
        rng,
      );
      const scoreToAdd = Math.round(timeAway / randomValue);
      if (scoreToAdd > 0) {
        bot.score += scoreToAdd;
        bot.modifiedAt = now;
      }
      break;
    }

    case 'lollipop': {
      throw new Error('Target races do not support lollipop goal.');
    }

    default:
      assertNever(config);
  }
}

/**
 * Update competitive bots on login.
 */
function updateCompetitiveBotsOnLogin(
  state: MutableState,
  botIndex: number,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  // Get race config.
  const { config } = getActiveRace(activeRaceState.createdAt)!;

  const raceType = config.type;
  switch (raceType) {
    case 'time':
      updateCompetitiveTimedRaceBotsOnLogin(
        state,
        botIndex,
        activeRaceState,
        bot,
        now,
      );
      break;

    case 'target':
      updateCompetitiveTargetRaceBotsOnLogin(
        state,
        botIndex,
        activeRaceState,
        bot,
        now,
      );
      break;

    default:
      assertNever(raceType);
  }
}

/**
 * Update timed race bots on login.
 */
function updateCompetitiveTimedRaceBotsOnLogin(
  state: MutableState,
  botIndex: number,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  const timeAway = Math.max(now - state.lastSessionAt, 0);
  const rng = createPrng(`${now + botIndex}`);

  switch (config.goal) {
    // If the player is offline -> bot will score additional points = ROUND(time the player was away / random integer between(20 and 30 minutes))
    // So if the player was away for 35 minutes and random int is 23, the score added will be 2. This is an addition on top of the existing score
    case 'level': {
      const randomValue = getRandomInt(
        duration({ minutes: 20 }),
        duration({ minutes: 30 }),
        rng,
      );
      const scoreToAdd = Math.round(timeAway / randomValue);
      if (scoreToAdd > 0) {
        bot.score += scoreToAdd;
        bot.modifiedAt = now;
      }
      break;
    }

    // If the player is offline -> bot will score additional points = ROUND(random integer between (15 and 45) * time the player was away / random integer between(20 and 30 minutes))
    // So if the player was away for 35 minutes, random score is 43 and random int is 11, the score added will be 136. This is an addition on top of the existing score
    case 'lollipop': {
      const randomValue = getRandomInt(
        duration({ minutes: 20 }),
        duration({ minutes: 30 }),
        rng,
      );
      const randomScore = getRandomInt(15, 45, rng);
      const scoreToAdd = Math.round((randomScore * timeAway) / randomValue);
      if (scoreToAdd > 0) {
        bot.score += scoreToAdd;
        bot.modifiedAt = now;
      }
      break;
    }

    default:
      assertNever(config);
  }
}

/**
 * Update target race bots on login.
 */
function updateCompetitiveTargetRaceBotsOnLogin(
  state: MutableState,
  botIndex: number,
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  const timeAway = Math.max(now - state.lastSessionAt, 0);
  const rng = createPrng(`${now + botIndex}`);

  switch (config.goal) {
    // If the player is offline -> bot will score additional points = time the player was away / random integer between(150 and 210 minutes)
    // So if the player was away for 200 minutes and random int is 121, the score added will be 2. This is an addition on top of the existing score
    case 'level': {
      const randomValue = getRandomInt(
        duration({ minutes: 150 }),
        duration({ minutes: 210 }),
        rng,
      );
      const scoreToAdd = Math.round(timeAway / randomValue);
      if (scoreToAdd > 0) {
        bot.score += Math.round(timeAway / randomValue);
        bot.modifiedAt = now;
      }
      break;
    }

    case 'lollipop': {
      throw new Error('Target races do not support lollipop goal.');
    }

    default:
      assertNever(config);
  }
}

/**
 * Update dud bots on login.
 */
function updateDudBotsOnLogin(
  activeRaceState: RaceState,
  bot: BotState,
  now: number,
) {
  // Get race config.
  const { config } = getActiveRace(activeRaceState.createdAt)!;
  switch (config.goal) {
    // Give 1 level every 30 minutes.
    case 'level': {
      const scoreToAdd = Math.floor(
        (now - bot.modifiedAt) / duration({ minutes: 30 }),
      );
      if (scoreToAdd > 0) {
        bot.score += scoreToAdd;
        bot.modifiedAt = now;
      }
      break;
    }

    // Give 10 sugar every 15 minutes.
    case 'lollipop': {
      const scoreToAdd =
        10 * Math.floor((now - bot.modifiedAt) / duration({ minutes: 15 }));
      if (scoreToAdd > 0) {
        bot.score += scoreToAdd;
        bot.modifiedAt = now;
      }
      break;
    }

    default:
      assertNever(config);
  }
}
